import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BookOpen,
  Search,
  Eye,
  Edit3,
  HelpCircle,
  FileCode,
  Layers,
} from 'lucide-react'
import AdminNav from '../components/AdminNav'
import { useConfig } from '../context/ConfigContext'
import { modules as builtInModules, SEED_COURSES as builtInCourses } from '../data/modules'
import { mergeCatalogue } from '../data/catalogue'

/**
 * Every lesson in the catalogue, under the module it belongs to.
 *
 * A lesson is one idea on one screen: it has no id, no URL and no score of its
 * own, so it can only ever be listed beneath its module — which is why this
 * page groups rather than paginating a flat list.
 *
 * It doubles as the coverage view. Lessons live in a module's `pages` array,
 * and only modules authored in AI Studio have one; the original modules are
 * bespoke React whose sections are markup rather than data. Those are listed
 * as unstructured instead of being hidden, because "which modules have no
 * lessons yet" is the useful question while the catalogue turns over.
 */
export default function AdminLessonsPage() {
  const { customModules, allCourses, loading } = useConfig()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCourse, setSelectedCourse] = useState('all')
  const [structure, setStructure] = useState('all') // 'all' | 'structured' | 'unstructured'

  const courses = allCourses || builtInCourses

  const catalogue = useMemo(
    () => mergeCatalogue(builtInModules, customModules || []),
    [customModules],
  )

  const stats = useMemo(() => {
    const structured = catalogue.filter((m) => m.lessons.length > 0)
    return {
      modules: catalogue.length,
      structured: structured.length,
      unstructured: catalogue.length - structured.length,
      lessons: structured.reduce((sum, m) => sum + m.lessons.length, 0),
    }
  }, [catalogue])

  const visible = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return catalogue.filter((mod) => {
      if (selectedCourse !== 'all' && mod.track !== selectedCourse) return false
      if (structure === 'structured' && mod.lessons.length === 0) return false
      if (structure === 'unstructured' && mod.lessons.length > 0) return false
      if (!q) return true
      const haystack = [
        mod.title,
        mod.subtitle,
        ...mod.lessons.flatMap((l) => [l.title, l.subtitle]),
      ]
      return haystack.some((text) => (text || '').toLowerCase().includes(q))
    })
  }, [catalogue, selectedCourse, structure, searchQuery])

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <AdminNav />

      <div className="mb-6 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <BookOpen className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lessons</h1>
          <p className="mt-1 text-sm text-gray-500">
            Every lesson in the catalogue, under the module it belongs to. A lesson is one idea on
            one screen with its own check questions — it has no score of its own, so it is always
            read and edited as part of its module.
          </p>
        </div>
      </div>

      {/* Coverage */}
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-2xl font-bold tabular-nums text-gray-900">{stats.lessons}</p>
          <p className="text-xs text-gray-500">lessons across {stats.structured} modules</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-2xl font-bold tabular-nums text-gray-900">
            {stats.structured}
            <span className="text-base font-medium text-gray-400">/{stats.modules}</span>
          </p>
          <p className="text-xs text-gray-500">modules with lessons in data</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <p className="text-2xl font-bold tabular-nums text-amber-900">{stats.unstructured}</p>
          <p className="text-xs text-amber-800">authored in code — no lessons to show yet</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-3 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search lesson or module titles…"
            className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-indigo-500 focus:outline-none"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 focus:border-indigo-500 focus:outline-none"
          >
            <option value="all">All courses</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={structure}
            onChange={(e) => setStructure(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 focus:border-indigo-500 focus:outline-none"
          >
            <option value="all">All modules ({stats.modules})</option>
            <option value="structured">With lessons ({stats.structured})</option>
            <option value="unstructured">Without lessons ({stats.unstructured})</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center text-sm text-gray-500">
          Nothing matches those filters.
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((mod) => {
            const course = courses.find((c) => c.id === mod.track)
            return (
              <div key={mod.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                {/* Module header */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/60 px-5 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-md border border-indigo-200/60 bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                        <Layers className="h-3 w-3" />
                        {course?.name || mod.track || 'Unfiled'}
                      </span>
                      {mod.phase && (
                        <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          {mod.phase}
                        </span>
                      )}
                      <span className="font-mono text-xs text-gray-400">ID: {mod.id}</span>
                    </div>
                    <h2 className="mt-1 truncate font-semibold text-gray-900">{mod.title}</h2>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-xs tabular-nums text-gray-500">
                      {mod.lessons.length || '—'} lesson{mod.lessons.length === 1 ? '' : 's'}
                    </span>
                    <Link
                      to={`/module/${mod.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      <Eye className="h-3.5 w-3.5 text-gray-500" /> Preview
                    </Link>
                    <Link
                      to={`/admin/studio/${mod.id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50/70 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                    >
                      <Edit3 className="h-3.5 w-3.5" /> Edit
                    </Link>
                  </div>
                </div>

                {/* Lessons */}
                {mod.lessons.length === 0 ? (
                  <p className="flex items-start gap-2 px-5 py-4 text-sm text-gray-500">
                    <FileCode className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <span>
                      Authored in code, so its sections are markup rather than data and cannot be
                      listed here. Opening it in AI Studio creates an editable version with real
                      lessons.
                    </span>
                  </p>
                ) : (
                  <ol className="divide-y divide-gray-100">
                    {mod.lessons.map((lesson, i) => {
                      const checks = Array.isArray(lesson.questions) ? lesson.questions.length : 0
                      return (
                        <li key={i} className="flex items-start gap-3 px-5 py-3">
                          <span className="mt-0.5 w-5 shrink-0 text-right font-mono text-xs tabular-nums text-gray-400">
                            {i + 1}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-900">
                              {lesson.title || `Lesson ${i + 1}`}
                            </span>
                            {lesson.subtitle && (
                              <span className="block text-xs text-gray-500">{lesson.subtitle}</span>
                            )}
                          </span>
                          <span
                            className={`inline-flex shrink-0 items-center gap-1 text-xs ${
                              checks === 0 ? 'text-amber-700' : 'text-gray-500'
                            }`}
                            title={
                              checks === 0
                                ? 'No check question — this lesson can be skipped past unread'
                                : `${checks} check question${checks === 1 ? '' : 's'}`
                            }
                          >
                            <HelpCircle className="h-3.5 w-3.5" />
                            {checks}
                          </span>
                        </li>
                      )
                    })}
                  </ol>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
